<template>
  <div class="public-league-view">
    <!-- Loading Overlay -->
    <LoadingOverlay :loading="isLoading" :message="loadingMessage" />

    <!-- Skeleton Loader for initial load -->
    <SkeletonLoader v-if="isInitialLoading" type="table" />

    <!-- Main Content -->
    <div v-show="!isInitialLoading" class="public-content">
      <div class="page-header">
        <div class="header-content">
          <div class="title-wrapper">
            <h1 class="text-h1 font-weight-bold page-title">
              {{ partnerName }} {{ tierLabel }} League
            </h1>
            <span class="tier-badge" :class="`tier-${tier}`">
              <img :src="currentTierIcon" :alt="tierLabel" class="tier-badge-icon" />
              <div class="tier-badge-label">{{ tierLabel }}</div>
            </span>
          </div>
          <p class="text-body-1 text--darken-2 page-subtitle">Public League Rankings</p>
        </div>
      </div>

      <div class="content-section">
        <!-- Podium Section -->
        <div v-if="!isLoading && top3Communities.length > 0" class="podium-section">
          <LeaguePodiumCard
            v-for="community in podiumOrder"
            :key="community.id"
            :community="community"
            :place="community.rank"
          />
        </div>

        <!-- Full Table -->
        <div v-if="!isLoading && communities.length > 0" class="table-section">
          <LeagueTable :communities="communities" :primary-color="primaryColor" />
        </div>

        <!-- Empty State -->
        <div v-if="!isLoading && communities.length === 0" class="empty-state">
          <span class="empty-state-icon"></span>
          <h3>No Data Available</h3>
          <p>This league tier is not available right now.</p>
        </div>
      </div>

      <!-- Powered by Swiset footer -->
      <div class="powered-by-footer">
        <p>Powered by <strong>Swiset</strong></p>
      </div>
    </div>
  </div>
</template>

<script>
import LeaguePodiumCard from '@/apps/broker/components/Leagues/LeaguePodiumCard.vue';
import LeagueTable from '@/apps/broker/components/Leagues/LeagueTable.vue';
import LoadingOverlay from '@/apps/broker/components/LoadingOverlay.vue';
import SkeletonLoader from '@/apps/broker/components/SkeletonLoader.vue';
import leaguesRepository from '@/apps/broker/repositories/leagues.repository';

export default {
  name: 'PublicLeagueView',
  components: {
    LeaguePodiumCard,
    LeagueTable,
    LoadingOverlay,
    SkeletonLoader
  },
  data() {
    return {
      partnerId: null,
      partnerName: '',
      tier: 'bronze',
      primaryColor: '#28EA96',
      isLoading: false,
      isInitialLoading: true,
      loadingMessage: 'Loading league data...',
      communities: []
    };
  },
  computed: {
    tierLabel() {
      const labels = {
        platinum: 'Platinum',
        gold: 'Gold',
        silver: 'Silver',
        bronze: 'Bronze'
      };
      return labels[this.tier] || 'Bronze';
    },
    currentTierIcon() {
      const icons = {
        platinum: require('@/apps/broker/designs/leagues/platinum-icon.png'),
        gold: require('@/apps/broker/designs/leagues/gold-icon.png'),
        silver: require('@/apps/broker/designs/leagues/silver-icon.png'),
        bronze: require('@/apps/broker/designs/leagues/bronze-icon.png')
      };
      return icons[this.tier];
    },
    tierAccentColor() {
      const colors = {
        platinum: '#E5E4E2',
        gold: '#FFD700',
        silver: '#C0C0C0',
        bronze: '#CD7F32'
      };
      return colors[this.tier];
    },
    tierApiMapping() {
      return {
        platinum: 'DIAMOND',
        gold: 'GOLD',
        silver: 'SILVER',
        bronze: 'BRONZE'
      };
    },
    top3Communities() {
      return this.communities.slice(0, 3).map((c, index) => ({
        ...c,
        rank: index + 1
      }));
    },
    podiumOrder() {
      if (this.top3Communities.length < 2) return this.top3Communities;
      const ordered = [];
      if (this.top3Communities[1]) ordered.push(this.top3Communities[1]); // 2nd
      if (this.top3Communities[0]) ordered.push(this.top3Communities[0]); // 1st
      if (this.top3Communities[2]) ordered.push(this.top3Communities[2]); // 3rd
      return ordered;
    }
  },
  async mounted() {
    // Get query parameters
    this.partnerId = parseInt(this.$route.query.partner);
    this.tier = this.$route.query.tier || 'bronze';

    if (!this.partnerId) {
      console.error('No partner ID provided');
      this.isInitialLoading = false;
      return;
    }

    await this.fetchLeagueData();
  },
  methods: {
    async fetchLeagueData() {
      this.isLoading = true;

      try {
        const leaderboardType = this.tierApiMapping[this.tier];
        const response = await leaguesRepository.getLeagueOverview(
          this.partnerId,
          'IBS_LEAGUE',
          leaderboardType
        );

        // Extract partner name from response
        this.partnerName = response.partnerName || 'Partner';

        // Map API response to component data structure
        this.communities = response.clubRanking.map(club => ({
          id: club.clubId,
          name: club.clubName,
          avatar: club.clubImageUrl,
          volume: club.totalVolume,
          points: null,
          rank: club.rank,
          memberCount: club.memberCount
        }));
      } catch (error) {
        console.error('Error fetching league data:', error);
        this.communities = [];
      } finally {
        this.isLoading = false;
        this.isInitialLoading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.public-league-view {
  min-height: 100vh;
  background-color: #F5F5F5;
  padding: 0;
}

.public-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 32px 24px;
}

.page-header {
  margin-bottom: 32px;

  .header-content {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .title-wrapper {
      display: flex;
      align-items: center;
      gap: 16px;
      flex-wrap: wrap;

      .page-title {
        font-family: 'Avenir', sans-serif;
        font-size: 32px;
        font-weight: 700;
        color: var(--v-mainBlack-base, #1A1A1A);
        margin: 0;
      }
    }

    .page-subtitle {
      font-family: 'Avenir', sans-serif;
      font-size: 16px;
      color: #666666;
      margin: 0;
    }
  }
}

.tier-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px;
  border-radius: 20px;
  font-family: 'Avenir', sans-serif;
  font-size: 14px;
  font-weight: 600;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(120deg, transparent 30%, rgba(255, 255, 255, 0.3) 50%, transparent 70%);
    background-size: 200% 100%;
    animation: shimmer 3s infinite;
  }

  .tier-badge-icon {
    width: 20px;
    height: 20px;
    position: relative;
    z-index: 1;
  }

  .tier-badge-label {
    position: relative;
    z-index: 1;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  &.tier-platinum {
    background: linear-gradient(135deg, #D4E4F7 0%, #F0F4F8 100%);

    .tier-badge-label {
      color: #2C5F8D !important;
    }
  }

  &.tier-gold {
    background: linear-gradient(135deg, #FFD700 0%, #FFF4CC 100%);

    .tier-badge-label {
      color: #8B6914 !important;
    }
  }

  &.tier-silver {
    background: linear-gradient(135deg, #C0C0C0 0%, #E8E8E8 100%);

    .tier-badge-label {
      color: #4A4A4A !important;
    }
  }

  &.tier-bronze {
    background: linear-gradient(135deg, #CD7F32 0%, #E8B88A 100%);

    .tier-badge-label {
      color: #3D1F0F !important;
    }
  }
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.content-section {
  background: #FFFFFF;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.podium-section {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 32px;
  align-items: end;
}

.table-section {
  margin-bottom: 32px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;

  .empty-state-icon {
    display: inline-block;
    width: 120px;
    height: 120px;
    background-color: v-bind(primaryColor);
    mask-image: url('@/apps/broker/designs/leagues/league.svg');
    mask-size: contain;
    mask-repeat: no-repeat;
    mask-position: center;
    -webkit-mask-image: url('@/apps/broker/designs/leagues/league.svg');
    -webkit-mask-size: contain;
    -webkit-mask-repeat: no-repeat;
    -webkit-mask-position: center;
    margin-bottom: 20px;
    opacity: 0.3;
  }

  h3 {
    font-family: 'Avenir', sans-serif;
    font-size: 24px;
    font-weight: 600;
    color: var(--v-mainBlack-base, #1A1A1A);
    margin: 0 0 8px 0;
  }

  p {
    font-family: 'Avenir', sans-serif;
    font-size: 16px;
    color: #666666;
    margin: 0;
    max-width: 500px;
    line-height: 1.6;
  }
}

.powered-by-footer {
  text-align: center;
  padding: 32px 0 16px;
  color: #999999;
  font-family: 'Avenir', sans-serif;
  font-size: 14px;

  p {
    margin: 0;
  }

  strong {
    color: #666666;
    font-weight: 600;
  }
}

// Responsive
@media (max-width: 768px) {
  .public-content {
    padding: 16px;
  }

  .page-header .header-content .title-wrapper .page-title {
    font-size: 24px;
  }

  .content-section {
    padding: 20px;
  }

  .podium-section {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}
</style>
